// Terug in de tijd: hoe zag deze notitie er eerder uit, en zet die stand desnoods terug.
//
// Links de momenten, rechts hoe het er toen uitzag. De oude tekst wordt getoond met dezelfde
// editor als de notitie zelf, maar op slot: zo ziet een codeblok er hier net zo uit als daar,
// zonder dat we een tweede manier van tonen hoeven te onderhouden.

import { useEffect, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import Image from "@tiptap/extension-image";
import { TableKit } from "@tiptap/extension-table";
import { api, GeenVerbinding, type Notitie, type Versie, type VersieRegel } from "../api.ts";
import { Icoon } from "./Icoon.tsx";
import { NotitieLink } from "./NotitieLink.ts";
import { Bijlage } from "./Bijlage.ts";
import { Callout } from "./Callout.ts";
import { Codeblok } from "./Codeblok.ts";

function wanneer(iso: string): string {
  const datum = new Date(iso);
  const nu = new Date();
  const minuten = Math.round((nu.getTime() - datum.getTime()) / 60_000);
  if (minuten < 1) return "Net";
  if (minuten < 60) return `${minuten} min geleden`;
  const tijd = datum.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
  if (datum.toDateString() === nu.toDateString()) return `Vandaag ${tijd}`;
  const gisteren = new Date(nu);
  gisteren.setDate(gisteren.getDate() - 1);
  if (datum.toDateString() === gisteren.toDateString()) return `Gisteren ${tijd}`;
  return `${datum.toLocaleDateString("nl-NL", { day: "numeric", month: "short" })} ${tijd}`;
}

type Props = {
  notitieId: string;
  sluit: () => void;
  opTeruggezet: (notitie: Notitie, moment: string) => void;
  opFout: (tekst: string) => void;
};

export function Versies({ notitieId, sluit, opTeruggezet, opFout }: Props) {
  const [regels, setRegels] = useState<VersieRegel[] | null>(null);
  const [gekozen, setGekozen] = useState<Versie | null>(null);
  const [bezig, setBezig] = useState(false);

  const kijker = useEditor({
    editable: false,
    // Dezelfde blokken als de editor zelf. Ook `NotitieLink`, want zonder die extensie
    // struikelt ProseMirror over een oude versie waar een `[[link]]` in staat.
    extensions: [StarterKit.configure({ codeBlock: false }), Codeblok, TaskList, TaskItem.configure({ nested: true }), Image.configure({ inline: false }), NotitieLink, Bijlage, Callout, TableKit.configure({ table: { resizable: false } })],
    editorProps: { attributes: { class: "tiptap" } },
    content: { type: "doc", content: [{ type: "paragraph" }] }
  });

  useEffect(() => {
    void (async () => {
      try {
        const { versies } = await api.versies(notitieId);
        setRegels(versies);
      } catch (fout) {
        setRegels([]);
        opFout(
          fout instanceof GeenVerbinding
            ? "Zonder verbinding zijn oude versies niet op te halen; ze staan op de server."
            : "De versies konden niet worden opgehaald."
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notitieId]);

  async function kies(regel: VersieRegel) {
    try {
      const versie = await api.versie(notitieId, regel.nr);
      setGekozen(versie);
      kijker?.commands.setContent(versie.inhoud ?? { type: "doc", content: [{ type: "paragraph" }] });
    } catch {
      opFout("Deze versie kon niet worden geopend.");
    }
  }

  async function zetTerug() {
    if (!gekozen || bezig) return;
    setBezig(true);
    try {
      const notitie = await api.zetVersieTerug(notitieId, gekozen.nr);
      opTeruggezet(notitie, wanneer(gekozen.gemaakt));
    } catch {
      opFout("Terugzetten is niet gelukt.");
    } finally {
      setBezig(false);
    }
  }

  return (
    <div className="overlay" onClick={sluit}>
      <div className="paneel versiepaneel" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Versiegeschiedenis">
        <div className="paneelkop">
          <Icoon naam="klok" grootte={19} />
          <strong style={{ flexGrow: 1 }}>Versies</strong>
          <button className="knop stil" onClick={sluit} aria-label="Sluiten">
            <Icoon naam="kruis" grootte={15} />
          </button>
        </div>

        <div className="versiebody">
          <div className="versielijst">
            {regels === null && <p className="leeg">Even ophalen…</p>}
            {regels?.length === 0 && (
              <p className="leeg">
                Nog geen oudere versies. Ze komen er vanzelf: bij elke schrijfsessie legt de app vast hoe de notitie
                ervóór was.
              </p>
            )}
            {regels?.map((regel) => (
              <button
                key={regel.nr}
                className="versieregel"
                aria-current={gekozen?.nr === regel.nr}
                onClick={() => void kies(regel)}
              >
                <span className="titel">{wanneer(regel.gemaakt)}</span>
                <span className="fragment">{regel.titel || "Zonder titel"}</span>
                {regel.fragment && <span className="fragment">{regel.fragment}</span>}
              </button>
            ))}
          </div>

          <div className="versieblad">
            {gekozen ? (
              <>
                <div className="versieblad-kop">
                  <span>
                    Zo zag hij eruit — <strong>{wanneer(gekozen.gemaakt)}</strong>
                  </span>
                  <button className="knop hoofd" onClick={() => void zetTerug()} disabled={bezig}>
                    {bezig ? "Bezig…" : "Terugzetten"}
                  </button>
                </div>
                <h3>{gekozen.titel || "Zonder titel"}</h3>
                <EditorContent editor={kijker} />
              </>
            ) : (
              <p className="leeg">Kies links een moment om te zien wat er toen stond.</p>
            )}
          </div>
        </div>

        <div className="paneelvoet">
          <span>Terugzetten maakt eerst een versie van wat er nú staat — ook dat is dus terug te draaien.</span>
        </div>
      </div>
    </div>
  );
}
